import React, { useEffect, useState, useMemo } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Banknote, Play, RotateCcw, CheckCircle2, AlertCircle, Clock,
  XCircle, Loader2, Search, Filter, BarChart3, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const STATUS_META = {
  scheduled: { color: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock, fr: "Programmé", en: "Scheduled" },
  processing: { color: "bg-blue-50 text-blue-700 border-blue-300", icon: Loader2, fr: "En cours", en: "Processing" },
  paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2, fr: "Payé", en: "Paid" },
  manual_paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2, fr: "Payé (manuel)", en: "Paid (manual)" },
  failed: { color: "bg-amber-50 text-amber-800 border-amber-300", icon: AlertCircle, fr: "Échec", en: "Failed" },
  abandoned: { color: "bg-red-50 text-red-700 border-red-300", icon: XCircle, fr: "Abandonné", en: "Abandoned" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}
function fmtAmount(eur) { return `${(eur || 0).toFixed(2)} €`; }

export default function AdminSepaInstallments() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [pendingMandates, setPendingMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [running, setRunning] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/sepa/installments"),
        api.get("/admin/sepa/finance-overview"),
        api.get("/admin/sepa/pending-mandates"),
      ]);
      setItems(a.data.items || []);
      setOverview(b.data);
      setPendingMandates(c.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const r = await api.post("/admin/sepa/run-reminders-now");
      toast.success(
        `${r.data.sent} relance(s) envoyée(s) · ${r.data.candidates} candidat(s)`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunningReminders(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (i.student_name || "").toLowerCase().includes(s)
        || (i.parent_email || "").toLowerCase().includes(s)
        || (i.parent_name || "").toLowerCase().includes(s);
    });
  }, [items, search, statusFilter]);

  const stats = useMemo(() => ({
    total: items.length,
    scheduled: items.filter(i => i.status === "scheduled").length,
    processing: items.filter(i => i.status === "processing").length,
    paid: items.filter(i => i.status === "paid" || i.status === "manual_paid").length,
    failed: items.filter(i => i.status === "failed" || i.status === "abandoned").length,
    sum_paid: items.filter(i => i.status === "paid" || i.status === "manual_paid")
                    .reduce((s, i) => s + (i.amount || 0), 0),
  }), [items]);

  const runCron = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/sepa/run-debits-now");
      toast.success(lang === "fr"
        ? `Cron exécuté · ${r.data.processed} échéance(s) traitée(s)`
        : `Cron run · ${r.data.processed} installment(s) processed`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const retry = async (id) => {
    try {
      const r = await api.post(`/admin/sepa/installments/${id}/retry`);
      toast.success(lang === "fr"
        ? `Tentative envoyée · statut: ${r.data.status}`
        : `Retry sent · status: ${r.data.status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markPaid = async (id) => {
    if (!window.confirm(lang === "fr"
      ? "Marquer cette échéance comme payée manuellement (virement, chèque...) ?"
      : "Mark this installment as paid manually (wire transfer, cheque...)?"))
      return;
    try {
      await api.post(`/admin/sepa/installments/${id}/mark-paid`);
      toast.success(lang === "fr" ? "Échéance marquée payée" : "Installment marked paid");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-sepa-installments">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl tracking-tight mb-1">
            {lang === "fr" ? "Prélèvements SEPA · échéances" : "SEPA installments"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lang === "fr"
              ? "Vue d'ensemble des prélèvements automatiques planifiés (plans 2× et 10×)."
              : "Overview of scheduled automatic direct debits (2× and 10× plans)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runReminders} disabled={runningReminders} data-testid="sepa-run-reminders-btn">
            {runningReminders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            {lang === "fr" ? "Relances mandats" : "Mandate reminders"}
          </Button>
          <Button onClick={runCron} disabled={running} data-testid="sepa-run-cron-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {lang === "fr" ? "Lancer le cron maintenant" : "Run cron now"}
          </Button>
        </div>
      </div>

      {/* Pending mandates needing reminders */}
      {pendingMandates.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-700" />
              <h2 className="font-semibold text-base">
                {lang === "fr"
                  ? `Mandats SEPA en attente (${pendingMandates.length})`
                  : `Pending SEPA mandates (${pendingMandates.length})`}
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {lang === "fr"
                ? "Ces familles ont payé l'acompte mais n'ont pas encore signé leur mandat SEPA. Des relances automatiques partent à J+3, J+7 et J+14 après l'acompte."
                : "These families paid the deposit but haven't signed their SEPA mandate yet. Automatic reminders are sent at D+3, D+7 and D+14 after the deposit."}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pending-mandates-table">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">{lang === "fr" ? "Élève" : "Student"}</th>
                    <th className="text-left p-2">{lang === "fr" ? "Parent" : "Parent"}</th>
                    <th className="text-left p-2">{lang === "fr" ? "Plan" : "Plan"}</th>
                    <th className="text-left p-2">{lang === "fr" ? "Acompte payé il y a" : "Deposit paid"}</th>
                    <th className="text-left p-2">{lang === "fr" ? "Relances" : "Reminders"}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingMandates.map(m => (
                    <tr key={m.enrollment_id} className="border-t" data-testid={`pending-mandate-${m.enrollment_id}`}>
                      <td className="p-2 font-medium">{m.student_name || "—"}</td>
                      <td className="p-2 text-xs text-muted-foreground">{m.parent_email}</td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-[10px]">
                          {m.payment_mode === "monthly" ? "10×" : m.payment_mode === "biannual" ? "2×" : m.payment_mode}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs">
                        {m.days_since_deposit !== null
                          ? (lang === "fr" ? `${m.days_since_deposit} jour(s)` : `${m.days_since_deposit} day(s)`)
                          : "—"}
                      </td>
                      <td className="p-2">
                        <Badge className={`text-[10px] ${m.reminder_count >= 3 ? "bg-red-100 text-red-700 border-red-300" : m.reminder_count > 0 ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                          {m.reminder_count}/3
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <StatBox label={lang === "fr" ? "Total" : "Total"} value={stats.total} />
        <StatBox label={lang === "fr" ? "Programmés" : "Scheduled"} value={stats.scheduled} />
        <StatBox label={lang === "fr" ? "En cours" : "Processing"} value={stats.processing} tone="info" />
        <StatBox label={lang === "fr" ? "Payés" : "Paid"} value={stats.paid} tone="success" />
        <StatBox label={lang === "fr" ? "Échec / abandonné" : "Failed / abandoned"} value={stats.failed} tone={stats.failed > 0 ? "danger" : "muted"} />
        <StatBox label={lang === "fr" ? "Encaissé" : "Collected"} value={`${stats.sum_paid.toFixed(0)} €`} tone="success" />
      </div>

      {/* Monthly chart — Expected vs Collected */}
      {overview && overview.months && overview.months.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-base">
                {lang === "fr" ? "Encaissement attendu vs réel par mois" : "Expected vs collected (monthly)"}
              </h2>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={overview.months} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v.toLocaleString()} €`} />
                <Tooltip formatter={(v) => `${Number(v).toLocaleString()} €`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="expected_amount" name={lang === "fr" ? "Attendu" : "Expected"} fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collected_amount" name={lang === "fr" ? "Encaissé" : "Collected"} fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="processing_amount" name={lang === "fr" ? "En cours" : "Processing"} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="failed_amount" name={lang === "fr" ? "Échec" : "Failed"} fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
              <KV label={lang === "fr" ? "Total attendu" : "Total expected"} value={`${overview.totals.expected_amount.toFixed(0)} €`} />
              <KV label={lang === "fr" ? "Total encaissé" : "Total collected"} value={`${overview.totals.collected_amount.toFixed(0)} €`} tone="success" />
              <KV label={lang === "fr" ? "En cours" : "Processing"} value={`${overview.totals.processing_amount.toFixed(0)} €`} tone="info" />
              <KV label={lang === "fr" ? "Échec" : "Failed"} value={`${overview.totals.failed_amount.toFixed(0)} €`} tone={overview.totals.failed_amount > 0 ? "danger" : "muted"} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={lang === "fr" ? "Élève, parent, email..." : "Student, parent, email..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="sepa-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 h-9 rounded-md border bg-card px-3 text-sm"
                data-testid="sepa-status-filter"
              >
                <option value="">{lang === "fr" ? "Tous les statuts" : "All statuses"}</option>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <option key={k} value={k}>{lang === "fr" ? m.fr : m.en}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm" data-testid="sepa-empty">
              {lang === "fr" ? "Aucune échéance ne correspond." : "No installment matches."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sepa-installments-table">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">{lang === "fr" ? "Élève" : "Student"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Parent" : "Parent"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Échéance" : "Installment"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Date prévue" : "Due date"}</th>
                    <th className="text-right p-3">{lang === "fr" ? "Montant" : "Amount"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Statut" : "Status"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Tentatives" : "Attempts"}</th>
                    <th className="text-right p-3 pr-4">{lang === "fr" ? "Actions" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(i => {
                    const meta = STATUS_META[i.status] || STATUS_META.scheduled;
                    const Icon = meta.icon;
                    const canRetry = i.status === "failed" || i.status === "abandoned" || i.status === "scheduled";
                    const canMarkPaid = i.status !== "paid" && i.status !== "manual_paid";
                    return (
                      <tr key={i.id} className="border-t hover:bg-muted/20" data-testid={`sepa-row-${i.id}`}>
                        <td className="p-3 font-medium">{i.student_name || "—"}</td>
                        <td className="p-3 text-xs text-muted-foreground">{i.parent_email || "—"}</td>
                        <td className="p-3">#{i.index}/{i.total}</td>
                        <td className="p-3">{fmtDate(i.due_date)}</td>
                        <td className="p-3 text-right font-medium">{fmtAmount(i.amount)}</td>
                        <td className="p-3">
                          <Badge className={`gap-1 text-[10px] ${meta.color}`}>
                            <Icon className={`w-3 h-3 ${i.status === "processing" ? "animate-spin" : ""}`} />
                            {lang === "fr" ? meta.fr : meta.en}
                          </Badge>
                          {i.last_failure_message && (
                            <div className="text-[10px] text-red-700 mt-1 truncate max-w-[200px]"
                                 title={i.last_failure_message}>
                              {i.last_failure_message}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-xs">{i.attempts || 0}/3</td>
                        <td className="p-3 text-right space-x-1">
                          {canRetry && (
                            <Button size="sm" variant="outline" onClick={() => retry(i.id)}
                                    data-testid={`sepa-retry-${i.id}`}>
                              <RotateCcw className="w-3 h-3 mr-1" />
                              {lang === "fr" ? "Réessayer" : "Retry"}
                            </Button>
                          )}
                          {canMarkPaid && (
                            <Button size="sm" variant="ghost" onClick={() => markPaid(i.id)}
                                    className="text-emerald-700"
                                    data-testid={`sepa-mark-paid-${i.id}`}>
                              <Banknote className="w-3 h-3 mr-1" />
                              {lang === "fr" ? "Payé manuellement" : "Mark paid"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ label, value, tone = "muted" }) {
  const tones = {
    muted: "border-muted bg-card",
    success: "border-emerald-200 bg-emerald-50",
    danger: "border-red-200 bg-red-50",
    info: "border-blue-200 bg-blue-50",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function KV({ label, value, tone = "muted" }) {
  const tones = {
    muted: "text-foreground",
    success: "text-emerald-700",
    danger: "text-red-700",
    info: "text-blue-700",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
